import type { QueryMeta, MutationMeta } from "@tanstack/react-query";
import { ApiClientError } from "@/services/api/client";

const NON_RETRYABLE_STATUS_CODES = [400, 401, 403, 404] as const;
type NonRetryableStatusCode = (typeof NON_RETRYABLE_STATUS_CODES)[number];

export function shouldRetryQuery(
  failureCount: number,
  error: unknown,
  maxRetries: number = 2
): boolean {
  if (failureCount >= maxRetries) {
    return false;
  }

  if (error instanceof ApiClientError) {
    if (
      typeof error.status === "number" &&
      (NON_RETRYABLE_STATUS_CODES as readonly number[]).includes(
        error.status as NonRetryableStatusCode
      )
    ) {
      return false;
    }
    return true;
  }

  return true;
}

export function isNonRetryableError(error: unknown): boolean {
  if (error instanceof ApiClientError) {
    return (
      typeof error.status === "number" &&
      (NON_RETRYABLE_STATUS_CODES as readonly number[]).includes(
        error.status as NonRetryableStatusCode
      )
    );
  }
  return false;
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiClientError) {
    return error.body?.detail || error.body?.message || error.message || "An error occurred";
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unexpected error occurred";
}

export function logQueryError(
  error: unknown,
  meta?:
    | QueryMeta
    | MutationMeta
    | { queryKey?: readonly unknown[]; mutationKey?: readonly unknown[] }
): void {
  if (import.meta.env.DEV) {
    const context = meta?.queryKey
      ? `Query: ${JSON.stringify(meta.queryKey)}`
      : meta?.mutationKey
        ? `Mutation: ${JSON.stringify(meta.mutationKey)}`
        : "Unknown query/mutation";

    console.error(`[Query Error] ${context}`, {
      error,
      message: getErrorMessage(error),
      status: error instanceof ApiClientError ? error.status : undefined,
    });
  }
}

export function createQueryRetryFunction(maxRetries: number = 2) {
  return (failureCount: number, error: unknown) =>
    shouldRetryQuery(failureCount, error, maxRetries);
}
